import React, { useState } from 'react';
import { z } from 'zod';
import { Content, Header, Page, SelectItem } from '@backstage/core-components';
import { Box, Button, TextField, Card, CardContent, CardHeader, Divider, Backdrop, CircularProgress, Grid } from '@material-ui/core';
import { useRequestService } from '../../services/RequestService';
import { DomainType, DomainStructure } from './types';
import { useNavigate } from 'react-router';
import { AlertMessage } from '../../components/alert';
import { AutocompleteInput, SelectInput } from '../../components/input';
import { useFormDefinition } from './useFormDefinition';

const domainStructureSchema = z.object({
    domainName: z.string()
        .nonempty('Name is required')
        .regex(/^[a-zA-Z0-9-]+$/, 'Only letters, numbers, and hyphens are allowed.'),
    domainTitle: z.string().nonempty('Title is required'),
    domainDescription: z.string().nonempty('Description is required'),
    domainType: z.string().nonempty('Type is required'),
    subdomainOf: z.string().optional(),
    teamProject: z.string().optional(),
}).superRefine((data, ctx) => {
    if (data.domainType !== 'bundle' && (!data.subdomainOf || data.subdomainOf.trim() === '')) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['subdomainOf'],
            message: 'Subdomain is required for this type.',
        });
    }
    if (data.domainType === 'product-area' && (!data.teamProject || data.teamProject.trim() === '')) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['teamProject'],
            message: 'Team Project is required for this type.',
        });
    }
});

const initialValues: DomainStructure = {
    domainName: '',
    domainTitle: '',
    domainDescription: '',
    domainType: '',
    subdomainOf: '',
    teamProject: ''
};

export const NewDomainStructurePage = () => {
    const navigate = useNavigate();
    const requestService = useRequestService();
    const { formValues, setFormValue, setFormValues, errors, isFormValid } = useFormDefinition(domainStructureSchema, initialValues);
    const domainTypes = [
        { value: 'bundle', label: 'Tower (e.g.: Supply, Agro)' },
        { value: 'product-area', label: 'Product Area (e.g.: Logística, Engenharia, Comercialização)' },
        { value: 'product-group', label: 'Product Group (e.g.: Fábrica, Transporte, Armazenagem)' },
    ];
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [domains, setDomains] = useState<SelectItem[]>([]);

    const handleChange = (field: string, value: any) => {
        setFormValue(field, value);
    };

    const handleTypeChange = async (field: string, value: DomainType) => {
        setFormValues(prevValues => ({
            ...prevValues,
            [field]: value,
            subdomainOf: '',
        }));
        
        if (value === 'bundle') 
            return;

        const subdomainOfMap: Record<string, DomainType> = {
            'product-area': 'bundle',
            'product-group': 'product-area',
        };

        setDomains([]);
        setDomains(await requestService.listDomains(subdomainOfMap[value]));
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            await requestService.createDomainStructure(formValues);
            navigate('/requests', { state: { message: 'Domain structure request created successfully!' } });
        } catch (err) {
            setError('Failed to submit domain request. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Page themeId="home">
            <Header title="New Domain Structure" type="New request" typeLink='/requests/new-request' />
            <Content>
                <Card>
                    <CardHeader title="Create a new domain structure" subheader="Fill out the form below to create a new domain structure request. Once submitted, you will be able to track the status of your request on the requests page." />
                    <CardContent>
                        <Divider />
                        <Box
                            component="form"
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            style={{ gap: '16px' }}
                            mt={3}
                        >
                            <Box width="100%" maxWidth="800px">
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <SelectInput 
                                                    label='Type' 
                                                    value={formValues.domainType} 
                                                    options={domainTypes}
                                                    onChange={value => handleTypeChange('domainType', value)}
                                                />
                                            </Grid>
                                            
                                            {['product-area', 'product-group'].includes(formValues.domainType) && (
                                                <Grid item xs={12} md={6}>
                                                        <AutocompleteInput 
                                                            label='Subdomain of' 
                                                            fieldName='subdomainOf' 
                                                            formValues={formValues} 
                                                            options={domains}
                                                            onChange={handleChange}
                                                        />
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Grid>
                                    
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Identifier"
                                            variant="outlined"
                                            placeholder="e.g.: supply, agro-comercializacao, transporte"
                                            value={formValues.domainName}
                                            onChange={(event) => handleChange('domainName', event.target.value)}
                                            required
                                            fullWidth
                                            error={!!errors.domainName}
                                            helperText={errors.domainName}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Name"
                                            variant="outlined"
                                            placeholder="e.g.: Supply, Agro Comercialização, Transporte"
                                            value={formValues.domainTitle}
                                            onChange={(event) => handleChange('domainTitle', event.target.value)}
                                            required
                                            fullWidth
                                            error={!!errors.domainTitle}
                                            helperText={errors.domainTitle}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            label="Description"
                                            variant="outlined"
                                            value={formValues.domainDescription}
                                            onChange={(event) => handleChange('domainDescription', event.target.value)}
                                            required
                                            fullWidth
                                            multiline
                                            minRows={4}
                                            error={!!errors.domainDescription}
                                            helperText={errors.domainDescription}
                                        />
                                    </Grid>

                                    {['product-area'].includes(formValues.domainType) && (
                                        <Grid item xs={12}>
                                            <TextField
                                                label="Team Project"
                                                variant="outlined"
                                                value={formValues.teamProject}
                                                onChange={(event) => handleChange('teamProject', event.target.value)}
                                                required
                                                fullWidth
                                                error={!!errors.teamProject}
                                                helperText={errors.teamProject}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        </Box>

                        <Box display="flex" justifyContent="flex-end" mt={3}>
                            <Button onClick={handleSubmit} disabled={!isFormValid} variant="contained" color="primary">
                                Submit
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </Content>

            <Backdrop open={loading} style={{ zIndex: 1300 }}>
                <CircularProgress color="primary" />
            </Backdrop>

            {error && <AlertMessage message={error} />}
        </Page>
    );
};
